import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Badge } from "../../../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../ui/table";
import {
  evidenceFindings,
  evidencePayloadDict,
  formatDateTime,
} from "./researchLibraryFormat";

const Muted = ({ children }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

export const Field = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd className="mt-0.5 text-sm break-words">{children ?? "—"}</dd>
  </div>
);

const OVERVIEW_LISTS = [
  ["explore", "What we'll explore"],
  ["practice", "What you'll practice"],
  ["expect", "What you can expect"],
];

const LONG_TEXT_FIELDS = [
  ["purpose", "Purpose"],
  ["methodology", "Dialogue methodology"],
  ["knowledge_payload", "Dialogue knowledge payload"],
  ["key_insights", "Key research-backed insights"],
  ["mechanisms", "Mechanisms / dynamics"],
  ["important_distinctions", "Important distinctions"],
  ["evidence_supported_approaches", "Evidence-supported approaches"],
  ["questions_to_explore", "Questions Aris should explore"],
  ["alternative_explanations", "Alternative explanations"],
  ["runtime_evidence_rules", "Runtime evidence rules"],
  ["how_informed", "How this dialogue is informed"],
  ["intersection_evidence", "Intersection evidence"],
  ["safety_boundary", "Safety boundary"],
  ["pts_policy", "Personalization contract"],
  ["faith_policy", "Faith integration"],
  ["mission_integration", "Mission integration"],
  ["eligibility_gate", "Eligibility gate"],
];

export const OverviewSection = ({ dialogue, overview }) => {
  const d = dialogue || {};
  const longFields = LONG_TEXT_FIELDS.filter(([key]) => d[key]);
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Description
        </p>
        <p className="mt-1 text-sm whitespace-pre-line">{d.description || "—"}</p>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Topic">{d.topic}</Field>
        <Field label="Situation family">{d.situation_family}</Field>
        <Field label="Role / population">{d.role}</Field>
        <Field label="Safety tier">{d.safety_tier}</Field>
        <Field label="Experience level">{d.experience_level}</Field>
        <Field label="Typical length">{d.typical_length}</Field>
        <Field label="Recommended rhythm">{d.recommended_rhythm}</Field>
        <Field label="Library version">{d.library_version}</Field>
        <Field label="Secondary life areas">
          {(d.secondary_life_areas || []).join(", ") || null}
        </Field>
        <Field label="Stages">{(d.stages || []).join(", ") || null}</Field>
        <Field label="Causal status">{d.causal_status}</Field>
        <Field label="Validation status">{d.research_validation_status}</Field>
      </dl>
      <div className="grid gap-4 md:grid-cols-3">
        {OVERVIEW_LISTS.map(([key, label]) => (
          <div key={key} className="rounded-md border p-3">
            <p className="mb-2 text-sm font-medium">{label}</p>
            {overview?.[key]?.length ? (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {overview[key].map((line, i) => (
                  <li key={i}>{typeof line === "string" ? line : line?.text || JSON.stringify(line)}</li>
                ))}
              </ul>
            ) : (
              <Muted>None</Muted>
            )}
          </div>
        ))}
      </div>
      {longFields.length > 0 && (
        <div className="space-y-2">
          {longFields.map(([key, label]) => (
            <details key={key} className="rounded-md border px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium">{label}</summary>
              <p className="mt-2 text-sm whitespace-pre-line">{d[key]}</p>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export const StagesSection = ({ foundations }) => {
  if (!foundations?.length) return <Muted>No foundations for this dialogue.</Muted>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Foundation</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Need</TableHead>
            <TableHead>How Aris uses the research</TableHead>
            <TableHead>Runtime safety disposition</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {foundations.map((f) => (
            <TableRow key={f.dialogue_id || `${f.stage}`} className="align-top">
              <TableCell className="font-mono text-xs whitespace-nowrap">
                {f.dialogue_id || "—"}
              </TableCell>
              <TableCell>{f.stage || "—"}</TableCell>
              <TableCell>{f.need || "—"}</TableCell>
              <TableCell className="min-w-[240px] text-sm">
                {f.how_aris_uses_research || "—"}
              </TableCell>
              <TableCell className="min-w-[200px] text-xs">
                {f.runtime_safety_disposition || "—"}
                {f.runtime_attachable === false && (
                  <Badge variant="warning" className="ml-2">
                    Held
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const EvidenceSection = ({ evidence }) => {
  const payload = evidencePayloadDict(evidence);
  if (!payload) return <Muted>No evidence payload for this dialogue.</Muted>;
  const findings = evidenceFindings(payload);
  const limitations = payload["Material Limitations / Do Not Infer"];
  return (
    <div className="space-y-4">
      {findings.length === 0 ? (
        <Muted>
          No certified findings (placeholder claims are not evidence Aris may cite).
        </Muted>
      ) : (
        <ol className="space-y-3">
          {findings.map((f) => (
            <li key={f.n} className="rounded-md border p-3 text-sm">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-semibold">Finding {f.n}</span>
                {f.claimId && (
                  <Badge variant="outline" className="font-mono">
                    {f.claimId}
                  </Badge>
                )}
                {f.sourceId && (
                  <Badge variant="secondary" className="font-mono">
                    {f.sourceId}
                  </Badge>
                )}
              </div>
              {f.headline && <p className="font-medium">{f.headline}</p>}
              <p className="whitespace-pre-line">{f.finding || "—"}</p>
              {(f.institution || f.title || f.evidenceType || f.url) && (
                <div className="mt-2 border-t pt-2 text-xs text-muted-foreground space-y-0.5">
                  {f.title && <div>{f.title}</div>}
                  {f.institution && <div>{f.institution}</div>}
                  {f.evidenceType && <div>{f.evidenceType}</div>}
                  {f.url && (
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                    >
                      {f.url} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <p className="font-medium">Material Limitations / Do Not Infer</p>
        <p className="mt-1 whitespace-pre-line">{limitations || "—"}</p>
      </div>
      {payload["Evidence Family IDs"] && (
        <p className="text-xs text-muted-foreground">
          Evidence family: {payload["Evidence Family IDs"]}
        </p>
      )}
    </div>
  );
};

export const ParentsSection = ({ parentIds }) => {
  if (!parentIds?.length) return <Muted>This dialogue has no composite parents.</Muted>;
  return (
    <ul className="flex flex-wrap gap-2">
      {parentIds.map((pid) => (
        <li key={pid}>
          <Link
            to={`/admin/research-library/${encodeURIComponent(pid)}`}
            className="inline-flex rounded-md border px-3 py-1.5 font-mono text-sm text-primary hover:bg-muted/40"
          >
            {pid}
          </Link>
        </li>
      ))}
    </ul>
  );
};

export const VisualsSection = ({ visuals }) => {
  if (!visuals?.length) return <Muted>No visual briefs for this dialogue.</Muted>;
  return (
    <div className="space-y-4">
      {visuals.map((v) => (
        <div key={v.asset_id} className="rounded-md border p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm">{v.asset_id}</span>
            <Badge variant="outline">{v.brief_approval_state || "Not approved"}</Badge>
          </div>
          <dl className="grid gap-3 md:grid-cols-2">
            {Object.entries(v.visual_brief || {}).map(([key, value]) => (
              <Field key={key} label={key.replace(/^Visual /, "")}>
                {typeof value === "string" ? value : JSON.stringify(value)}
              </Field>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};

export const OverridesNote = ({ overrides }) => {
  if (!overrides) return null;
  const parts = [];
  if (overrides.member_title != null) parts.push("title");
  if (overrides.description != null) parts.push("description");
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
      <p className="font-medium">Admin overrides</p>
      <ul className="mt-1 space-y-0.5 text-muted-foreground">
        {parts.length > 0 && <li>Overrides the release's {parts.join(" and ")}.</li>}
        {overrides.hold && (
          <li>
            Held by {overrides.hold.by || "an admin"} ·{" "}
            {formatDateTime(overrides.hold.at)} — “{overrides.hold.reason}”
          </li>
        )}
        {overrides.updated_by && (
          <li>
            Last updated by {overrides.updated_by} · {formatDateTime(overrides.updated_at)}
          </li>
        )}
      </ul>
    </div>
  );
};
